import React, { useEffect, useState } from 'react';
import { useUser } from '../../hooks/useUser';
import { useAppSession } from '../../contexts/AppSessionContext';
import {
  ImportBatch,
  ImportBatchDetails,
  ImportWorkerInfo,
  BatchComparison,
  getImportBatches,
  getImportBatchDetails,
  getImportWorkers,
  approveBatch,
  rejectBatch,
  deleteBatch,
  purgeStagingRows,
  uploadImportFiles,
  downloadImportTemplate,
  compareBatch,
  ImportFilesByRole,
} from '../../services/importService';
import { useAccountStore } from '../../components/Stores/accountStore';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import { useFx } from '../../hooks/useFx';
import { useBaseCurrency } from '../../hooks/useBaseCurrency';
import {
  CheckCircle,
  Cancel,
  Refresh,
  ExpandMore,
  ExpandLess,
  Inventory2,
  LocalShipping,
  ShoppingCart,
  AccountBalance,
  Warning,
  ErrorOutline,
  DeleteOutline,
  CleaningServices,
  CloudUpload,
  Analytics,
  Percent,
  FileDownloadOutlined,
  TrendingUp,
  CompareArrows,
} from '@mui/icons-material';
import ImportAnalysisDialog from './ImportAnalysisDialog';

const ImportBatches: React.FC = () => {
  const { data: currentUser } = useUser();
  const { user: sessionUser } = useAppSession();
  const { formulaColorDictionary: colorsMap } = useAccountStore();
  useCardBar({ visible: false });
  const baseCurrency = useBaseCurrency();
  const fx = useFx(baseCurrency);
  const accountId = sessionUser?.accountId ? parseInt(sessionUser.accountId) : undefined;

  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [expandedBatchId, setExpandedBatchId] = useState<number | null>(null);
  const [batchDetails, setBatchDetails] = useState<ImportBatchDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [confirmAction, setConfirmAction] = useState<{
    type: 'approve' | 'reject' | 'delete' | 'purge';
    batchId: number;
    batchName: string;
  } | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [approveMode, setApproveMode] = useState<'replace' | 'add'>('replace');
  const [processing, setProcessing] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');

  // Analysis dialog state
  const [analysisOpen, setAnalysisOpen] = useState(false);

  // Comparison state
  const [comparison, setComparison] = useState<BatchComparison | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState<string | null>(null);

  // Upload state — one file per role
  const [showUpload, setShowUpload] = useState(false);
  const [roleFiles, setRoleFiles] = useState<ImportFilesByRole>({});
  const [uploading, setUploading] = useState(false);
  // Client-side file role detection: filename → detected role
  const [fileDetections, setFileDetections] = useState<Record<string, string | null>>({});

  // Worker selection
  const [workers, setWorkers] = useState<ImportWorkerInfo[]>([]);
  const [selectedWorker, setSelectedWorker] = useState<string>('');

  // Fetch available workers for this account
  useEffect(() => {
    getImportWorkers(accountId).then((w) => {
      setWorkers(w);
      if (w.length > 0 && !selectedWorker) setSelectedWorker(w[0].name);
    }).catch(console.error);
  }, [accountId]);

  const currentWorker = workers.find(w => w.name === selectedWorker);

  // Role icon/accept lookup
  const ROLE_ICONS: Record<string, React.ReactNode> = {
    contracts: <ShoppingCart sx={{ fontSize: 18 }} />,
    po: <LocalShipping sx={{ fontSize: 18 }} />,
    inventory: <Inventory2 sx={{ fontSize: 18 }} />,
    gl: <AccountBalance sx={{ fontSize: 18 }} />,
    data: <CloudUpload sx={{ fontSize: 18 }} />,
    files: <CloudUpload sx={{ fontSize: 18 }} />,
    pdf: <Analytics sx={{ fontSize: 18 }} />,
  };
  const ROLE_LABELS: Record<string, string> = {
    contracts: 'Contracts',
    po: 'Purchase Orders',
    inventory: 'Inventory',
    gl: 'General Ledger',
    data: 'Data File',
    files: 'Import Files',
    pdf: 'Broker Statement (PDF)',
  };
  const ROLE_ACCEPT: Record<string, string> = {
    contracts: '.csv',
    po: '.csv',
    inventory: '.csv',
    gl: '.csv,.xlsx,.xls',
    data: '.xlsx,.xls',
    files: '.csv',
    pdf: '.pdf',
  };

  const handleRoleFileSelect = (role: string, file: File | undefined) => {
    setRoleFiles((prev) => {
      const next = { ...prev };
      if (file) next[role] = file;
      else delete next[role];
      return next;
    });
  };

  /** Detect CSV role by reading first line of a File in-browser.
   *  Handles both UTF-8 and cp1255 encoded files. */
  const detectCsvRole = async (file: File): Promise<string | null> => {
    try {
      const slice = file.slice(0, 2048);
      // Try UTF-8 first
      const text = await slice.text();
      const header = text.split(/\r?\n/)[0];
      if (header.includes('\u05D1\u05DC\u05EA\u05D9-\u05DE\u05D5\u05D2\u05D1\u05DC')) return 'inventory';
      if (header.includes('\u05EA\u05D0\u05E8\u05D9\u05DA \u05E8\u05D9\u05E9\u05D5\u05DD')) return 'gl';
      if (header.includes('\u05D8\u05E7\u05E1\u05D8 \u05E7\u05E6\u05E8')) return 'contracts';
      if (header.includes('\u05E1\u05D5\u05D2 \u05DE\u05E1\u05DE\u05DA \u05E8\u05DB\u05D9\u05E9\u05D4')) return 'po';
      // Fallback: read raw bytes for cp1255 detection
      const buf = await file.slice(0, 2048).arrayBuffer();
      const bytes = new Uint8Array(buf);
      const raw = Array.from(bytes).map(b => String.fromCharCode(b)).join('');
      const rawHeader = raw.split(/\r?\n/)[0];
      if (rawHeader.includes('\xE1\xEC\xFA\xE9-\xEE\xE5\xE2\xE1\xEC')) return 'inventory';
      if (rawHeader.includes('\xFA\xE0\xF8\xE9\xEA \xF8\xE9\xF9\xE5\xED')) return 'gl';
      if (rawHeader.includes('\xE8\xF7\xF1\xE8 \xF7\xF6\xF8')) return 'contracts';
      if (rawHeader.includes('\xF1\xE5\xE2 \xEE\xF1\xEE\xEA \xF8\xEB\xE9\xF9\xE4')) return 'po';
    } catch { /* ignore */ }
    return null;
  };

  const handleMultiFileSelect = async (role: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fileArr = Array.from(files);
    setRoleFiles((prev) => ({ ...prev, [role]: fileArr }));
    // Detect roles for each file
    const detections: Record<string, string | null> = {};
    for (const f of fileArr) {
      detections[f.name] = await detectCsvRole(f);
    }
    setFileDetections((prev) => ({ ...prev, ...detections }));
  };

  const removeMultiFile = (role: string, index: number) => {
    setRoleFiles((prev) => {
      const next = { ...prev };
      const arr = next[role];
      if (Array.isArray(arr)) {
        const removed = arr[index];
        if (removed) setFileDetections((d) => { const n = { ...d }; delete n[removed.name]; return n; });
        const updated = arr.filter((_, i) => i !== index);
        if (updated.length === 0) delete next[role];
        else next[role] = updated;
      }
      return next;
    });
  };

  // For multi-file roles, check files are provided (and sub-roles detected for workers that need them)
  const detectedRoles = new Set(Object.values(fileDetections).filter(Boolean));
  const requiredRolesFilled = currentWorker
    ? currentWorker.roles.filter(r => r.required).every(r => {
        const val = roleFiles[r.role];
        if (!val) return false;
        if (Array.isArray(val)) {
          if (val.length === 0) return false;
          // Only check sub-role detection for workers expecting multiple files (e.g. Strauss)
          if (r.maxFiles && r.maxFiles > 1) {
            const expectedSubRoles = (currentWorker.expectedFiles || []).filter((f: any) => f.required).map((f: any) => f.name.replace(/\.[^.]+$/, ''));
            return expectedSubRoles.every((k: string) => detectedRoles.has(k));
          }
          return true;
        }
        return true;
      })
    : false;
  const totalFilesSelected = Object.values(roleFiles).reduce((sum, v) => sum + (Array.isArray(v) ? v.length : (v ? 1 : 0)), 0);

  const handleUpload = async () => {
    if (!accountId || !requiredRolesFilled) return;
    setUploading(true);
    setMessage(null);
    try {
      const result = await uploadImportFiles(roleFiles, accountId, selectedWorker);
      const supplyCount = result.summary?.supplyPositionsCreated || 0;
      const demandCount = result.summary?.demandPositionsCreated || 0;
      const hedgeCount = result.summary?.hedgePositionsCreated || 0;
      const parts = [];
      if (supplyCount > 0) parts.push(`${supplyCount} supply`);
      if (demandCount > 0) parts.push(`${demandCount} demand`);
      if (hedgeCount > 0) parts.push(`${hedgeCount} hedge`);
      setMessage({ type: 'success', text: `Import complete! Batch #${result.summary?.batchId} created with ${parts.join(' + ') || '0'} positions.` });
      setShowUpload(false);
      setRoleFiles({});
      setFileDetections({});
      loadBatches();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  // Load batches
  const loadBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getImportBatches(accountId, filterStatus || undefined);
      setBatches(data);
    } catch (err: any) {
      console.error('Error fetching batches:', err);
      setError(err.message || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.id && accountId) {
      loadBatches();
    }
  }, [currentUser?.id, accountId, filterStatus]);

  // Load batch details when expanded
  const toggleExpand = async (batchId: number) => {
    if (expandedBatchId === batchId) {
      setExpandedBatchId(null);
      setBatchDetails(null);
      return;
    }

    setExpandedBatchId(batchId);
    setComparison(null);
    setComparisonError(null);
    setDetailsLoading(true);
    try {
      const details = await getImportBatchDetails(batchId);
      setBatchDetails(details);
    } catch (err: any) {
      console.error('Error fetching batch details:', err);
      setError(err.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Approve batch
  const handleApprove = async () => {
    if (!confirmAction || confirmAction.type !== 'approve') return;
    setProcessing(true);
    setMessage(null);
    try {
      const result = await approveBatch(confirmAction.batchId, approveMode);
      const msg = approveMode === 'add'
        ? `Batch #${confirmAction.batchId} approved (added to existing data).`
        : `Batch #${confirmAction.batchId} approved! Replaced ${result.deletedBatches} old batch(es), ${result.deletedSupply} old supply positions.`;
      setMessage({ type: 'success', text: msg });
      setConfirmAction(null);
      setApproveMode('replace');
      loadBatches();
      if (expandedBatchId === confirmAction.batchId) {
        const details = await getImportBatchDetails(confirmAction.batchId);
        setBatchDetails(details);
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to approve batch' });
    } finally {
      setProcessing(false);
    }
  };

  // Reject batch
  const handleReject = async () => {
    if (!confirmAction || confirmAction.type !== 'reject') return;
    setProcessing(true);
    setMessage(null);
    try {
      await rejectBatch(confirmAction.batchId, rejectComment || undefined);
      setMessage({ type: 'success', text: `Batch #${confirmAction.batchId} rejected.` });
      setConfirmAction(null);
      setRejectComment('');
      loadBatches();
      if (expandedBatchId === confirmAction.batchId) {
        const details = await getImportBatchDetails(confirmAction.batchId);
        setBatchDetails(details);
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to reject batch' });
    } finally {
      setProcessing(false);
    }
  };

  // Delete batch
  const handleDelete = async () => {
    if (!confirmAction || confirmAction.type !== 'delete') return;
    setProcessing(true);
    setMessage(null);
    try {
      await deleteBatch(confirmAction.batchId);
      setMessage({ type: 'success', text: `Batch #${confirmAction.batchId} deleted.` });
      setConfirmAction(null);
      if (expandedBatchId === confirmAction.batchId) {
        setExpandedBatchId(null);
        setBatchDetails(null);
      }
      loadBatches();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete batch' });
    } finally {
      setProcessing(false);
    }
  };

  // Purge staging rows
  const handlePurge = async () => {
    if (!confirmAction || confirmAction.type !== 'purge') return;
    setProcessing(true);
    setMessage(null);
    try {
      const purged = await purgeStagingRows(confirmAction.batchId);
      setMessage({ type: 'success', text: `Purged ${purged} staging rows from batch #${confirmAction.batchId}.` });
      setConfirmAction(null);
      if (expandedBatchId === confirmAction.batchId) {
        const details = await getImportBatchDetails(confirmAction.batchId);
        setBatchDetails(details);
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to purge staging rows' });
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (value: string | null) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const getReviewBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f59e0b', display: 'inline-block' }} />
            Pending Review
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}>
            <CheckCircle sx={{ fontSize: 12 }} />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}>
            <Cancel sx={{ fontSize: 12 }} />
            Rejected
          </span>
        );
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: '#eff6ff', color: '#1d4ed8' }}>Completed</span>;
      case 'failed':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }}>Failed</span>;
      case 'processing':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: '#f5f3ff', color: '#6d28d9' }}>Processing</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-700">{status}</span>;
    }
  };

  const getSupplyTypeLabel = (type: string) => {
    switch (type) {
      case 'purchase_order':
        return <span className="inline-flex items-center gap-1 text-gray-700"><LocalShipping sx={{ fontSize: 16 }} />Purchase Orders</span>;
      case 'on_hand_inventory':
        return <span className="inline-flex items-center gap-1 text-gray-700"><Inventory2 sx={{ fontSize: 16 }} />On-Hand Inventory</span>;
      case 'ratio_po':
        return <span className="inline-flex items-center gap-1 text-gray-400"><Percent sx={{ fontSize: 16 }} />Ratio POs</span>;
      default: return type;
    }
  };

  const FALLBACK_COLORS = ['#5467B2', '#BB62B0', '#7A67B6', '#6574C5', '#0083B1', '#1FA0D7', '#2866A8', '#6799E0'];
  const formulaColorCache: Record<string, string> = {};
  const getFormulaColor = (formulaCode: string) => {
    if (colorsMap[formulaCode]) return colorsMap[formulaCode];
    if (!formulaColorCache[formulaCode]) {
      const idx = Object.keys(formulaColorCache).length;
      formulaColorCache[formulaCode] = FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
      console.log(`[ImportBatches] Formula color fallback: "${formulaCode}" not in colorsMap (keys: ${Object.keys(colorsMap).join(', ')}). Using fallback ${formulaColorCache[formulaCode]}`);
    }
    return formulaColorCache[formulaCode];
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Batches</h1>
          <p className="text-sm text-gray-500 mt-1">Review and approve data import batches</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#4f46e5' }}
          >
            <CloudUpload fontSize="small" />
            Upload Files
          </button>
          <button
            onClick={loadBatches}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Refresh fontSize="small" className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Upload panel — one slot per role */}
      {showUpload && (
        <div className="mb-4 p-5 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Upload Import Files</h3>
              <p className="text-xs text-gray-500 mt-0.5">Select the import worker and upload the required files.</p>
            </div>
            <button
              onClick={() => { setShowUpload(false); setRoleFiles({}); setFileDetections({}); }}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>

          {/* Worker selector tabs */}
          {workers.length > 1 && (
            <div className="flex gap-2 mb-4">
              {workers.map((w) => (
                <button
                  key={w.name}
                  onClick={() => { setSelectedWorker(w.name); setRoleFiles({}); setFileDetections({}); }}
                  style={selectedWorker === w.name ? { backgroundColor: '#4f46e5', color: '#fff' } : {}}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    selectedWorker === w.name
                      ? 'shadow-sm'
                      : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          )}

          {/* Download template button — shown for manual worker */}
          {selectedWorker === 'manual' && (
            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-indigo-800">Step 1:</span>
                <span className="text-sm text-indigo-700 ml-1">Download the Excel template, fill it in, then upload below.</span>
              </div>
              <button
                onClick={() => downloadImportTemplate(accountId)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                <FileDownloadOutlined sx={{ fontSize: 16 }} />
                Download Template
              </button>
            </div>
          )}

          <div className={`grid ${currentWorker && currentWorker.roles.length === 1 ? 'grid-cols-1 max-w-md' : 'grid-cols-2'} gap-3 mb-4`}>
            {currentWorker?.roles.map((role) => {
              const val = roleFiles[role.role];
              const icon = ROLE_ICONS[role.role] || <CloudUpload sx={{ fontSize: 18 }} />;
              const label = ROLE_LABELS[role.role] || role.role;
              const accept = ROLE_ACCEPT[role.role] || '.xlsx,.xls,.csv';
              const isMulti = role.multiple;
              const multiFiles = Array.isArray(val) ? val : [];
              const singleFile = !Array.isArray(val) ? val : undefined;
              const hasFiles = isMulti ? multiFiles.length > 0 : !!singleFile;

              return (
                <div key={role.role} className={isMulti ? 'col-span-full' : ''}>
                  <label
                    className={`relative flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                      hasFiles
                        ? 'border-green-300 bg-green-50'
                        : role.required
                          ? 'border-dashed border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
                          : 'border-dashed border-gray-200 bg-gray-50/50 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="file"
                      accept={accept}
                      multiple={isMulti}
                      className="hidden"
                      onChange={(e) => {
                        if (isMulti) {
                          handleMultiFileSelect(role.role, e.target.files);
                        } else {
                          handleRoleFileSelect(role.role, e.target.files?.[0]);
                        }
                        e.target.value = '';
                      }}
                    />
                    <span className={`shrink-0 ${hasFiles ? 'text-green-600' : 'text-gray-400'}`}>
                      {icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-700">{label}</span>
                        {role.required && !hasFiles && (
                          <span className="text-[10px] text-red-400 font-medium">required</span>
                        )}
                        {!role.required && (
                          <span className="text-[10px] text-gray-400">optional</span>
                        )}
                      </div>
                      {isMulti && !hasFiles && (
                        <span className="text-xs text-gray-400 mt-0.5">Click to select up to {role.maxFiles || 4} {accept === '.pdf' ? 'PDF' : 'CSV'} files (auto-detected)</span>
                      )}
                      {!isMulti && singleFile && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-xs text-green-700 font-medium truncate">{singleFile.name}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRoleFileSelect(role.role, undefined); }}
                            className="text-[10px] text-red-400 hover:text-red-600 shrink-0"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                      {!isMulti && !singleFile && (
                        <span className="text-xs text-gray-400 mt-0.5">Click to select {accept}</span>
                      )}
                    </div>
                    {hasFiles && !isMulti && (
                      <CheckCircle sx={{ fontSize: 16 }} className="text-green-500 shrink-0" />
                    )}
                    {isMulti && hasFiles && (
                      <span className="text-xs text-green-700 font-semibold shrink-0">{multiFiles.length} files</span>
                    )}
                  </label>
                  {isMulti && multiFiles.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2 px-1">
                      {multiFiles.map((f, idx) => {
                        const detected = fileDetections[f.name];
                        const roleLabel = detected ? (ROLE_LABELS[detected] || detected) : null;
                        const roleColor = detected
                          ? { contracts: '#4f46e5', po: '#0891b2', inventory: '#059669', gl: '#d97706' }[detected] || '#6b7280'
                          : '#ef4444';
                        return (
                          <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                            {f.name}
                            {roleLabel ? (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white" style={{ backgroundColor: roleColor }}>
                                {roleLabel}
                              </span>
                            ) : detected === null && f.name in fileDetections ? (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white bg-red-400">
                                Unknown
                              </span>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => removeMultiFile(role.role, idx)}
                              className="text-red-400 hover:text-red-600 ml-0.5"
                            >
                              ✕
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleUpload}
              disabled={uploading || !requiredRolesFilled}
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-40 hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#16a34a' }}
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CloudUpload sx={{ fontSize: 16 }} />
                  Import {totalFilesSelected > 0 ? `(${totalFilesSelected} files)` : ''}
                </>
              )}
            </button>
            {!requiredRolesFilled && totalFilesSelected > 0 && currentWorker && (() => {
              // For multi-file roles with sub-role detection, show which are missing
              const multiRole = currentWorker.roles.find(r => r.multiple && r.maxFiles && r.maxFiles > 1);
              if (multiRole && totalFilesSelected > 0) {
                const expectedSubRoles = (currentWorker.expectedFiles || []).filter((f: any) => f.required).map((f: any) => f.name.replace(/\.[^.]+$/, ''));
                const missing = expectedSubRoles.filter((k: string) => !detectedRoles.has(k));
                if (missing.length > 0) {
                  return (
                    <span className="text-xs text-orange-500">
                      Missing: {missing.map((k: string) => ROLE_LABELS[k] || k).join(', ')}
                    </span>
                  );
                }
                return null;
              }
              const missingRoles = currentWorker.roles.filter((r) => {
                if (!r.required) return false;
                const v = roleFiles[r.role];
                if (!v) return true;
                if (Array.isArray(v)) return v.length === 0;
                return false;
              });
              if (missingRoles.length === 0) return null;
              return (
                <span className="text-xs text-orange-500">
                  {missingRoles.map((r) => ROLE_LABELS[r.role] || r.role).join(', ')} still needed
                </span>
              );
            })()}
          </div>
        </div>
      )}

      {/* Message banner */}
      {message && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right text-lg leading-none font-bold opacity-50 hover:opacity-100">
            ×
          </button>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {['', 'pending', 'approved', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filterStatus === status
                ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {status === '' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {/* Empty state */}
      {!loading && batches.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Inventory2 sx={{ fontSize: 48 }} className="mb-2 opacity-30" />
          <p>No import batches found</p>
        </div>
      )}

      {/* Batches list */}
      {!loading && batches.length > 0 && (
        <div className="space-y-3">
          {batches.map((batch) => (
            <div key={batch.id}
              className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
              style={{
                borderLeft: `3px solid ${
                  batch.review_status === 'approved' ? '#22c55e'
                  : batch.review_status === 'rejected' ? '#ef4444'
                  : batch.review_status === 'pending' ? '#f59e0b'
                  : '#d1d5db'
                }`,
              }}>
              {/* Batch row */}
              <div
                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(batch.id)}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="text-gray-400">
                    {expandedBatchId === batch.id ? <ExpandLess /> : <ExpandMore />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">#{batch.id}</span>
                      <span className="text-gray-500 text-sm truncate">{batch.original_filename || 'Import batch'}</span>
                      {getReviewBadge(batch.review_status)}
                      {getStatusBadge(batch.status)}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      <span>{formatDate(batch.created_at)}</span>
                      {batch.created_by_name && <span>by {batch.created_by_name}</span>}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6 flex-shrink-0">
                  {/* Counts */}
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    {batch.supply_count > 0 && (
                      <div className="flex items-center gap-1" title="Supply positions">
                        <LocalShipping fontSize="small" />
                        <span>{batch.supply_count}</span>
                      </div>
                    )}
                    {batch.demand_count > 0 && (
                      <div className="flex items-center gap-1" title="Demand positions">
                        <ShoppingCart fontSize="small" />
                        <span>{batch.demand_count}</span>
                      </div>
                    )}
                    {batch.hedge_count > 0 && (
                      <div className="flex items-center gap-1" title="Hedge positions">
                        <Analytics fontSize="small" />
                        <span>{batch.hedge_count}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {batch.review_status === 'pending' && batch.status === 'completed' && (
                      <>
                        <button
                          onClick={() => setConfirmAction({ type: 'approve', batchId: batch.id, batchName: batch.original_filename || `#${batch.id}` })}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          <CheckCircle fontSize="small" />
                          Approve
                        </button>
                        <button
                          onClick={() => setConfirmAction({ type: 'reject', batchId: batch.id, batchName: batch.original_filename || `#${batch.id}` })}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <Cancel fontSize="small" />
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setConfirmAction({ type: 'delete', batchId: batch.id, batchName: batch.original_filename || `#${batch.id}` })}
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete batch"
                    >
                      <DeleteOutline fontSize="small" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Error summary */}
              {batch.error_summary && (
                <div className="mx-5 mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  <ErrorOutline fontSize="small" className="mr-1 align-text-bottom" />
                  {batch.error_summary}
                </div>
              )}

              {/* Review comment */}
              {batch.review_comment && (
                <div className="mx-5 mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                  <strong>Review comment:</strong> {batch.review_comment}
                </div>
              )}

              {/* Expanded details */}
              {expandedBatchId === batch.id && (
                <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                  {detailsLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : batchDetails ? (
                    <div className="space-y-4">
                      {/* Supply summary table (non-manual workers) */}
                      {batchDetails.metadata?.workerName !== 'manual' && batchDetails.supplySummary.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">Supply Positions</h3>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                <th className="pb-2 pr-4">Formula</th>
                                <th className="pb-2 pr-4">Type</th>
                                <th className="pb-2 pr-4 text-right">Count</th>
                                <th className="pb-2 pr-4 text-right">Quantity (MT)</th>
                                <th className="pb-2 text-right">Avg Price</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {(() => {
                                // DB returns rows split by currency. Merge rows with same formula+supply_type.
                                const grouped: Record<string, typeof batchDetails.supplySummary> = {};
                                for (const r of batchDetails.supplySummary) {
                                  const k = `${r.formula_code}|${r.supply_type}`;
                                  if (!grouped[k]) grouped[k] = [];
                                  grouped[k].push(r);
                                }
                                let anyMixed = false;
                                return Object.entries(grouped).map(([key, rows]) => {
                                  const first = rows[0];
                                  const totalCount = rows.reduce((s, r) => s + r.count, 0);
                                  const totalRem = rows.reduce((s, r) => s + parseFloat(r.total_remaining || '0'), 0);
                                  const totalQty = rows.reduce((s, r) => s + parseFloat(r.total_quantity || '0'), 0);
                                  const qty = totalRem || totalQty;
                                  const currencies = new Set(rows.filter(r => r.avg_price && r.price_currency).map(r => (r.price_currency || '').toUpperCase()));
                                  const isMixed = currencies.size > 1;
                                  if (isMixed) anyMixed = true;

                                  let priceDisplay: React.ReactNode = '—';
                                  // For ratio POs, show weighted avg ratio instead of price
                                  if (first.supply_type === 'ratio_po') {
                                    const ratioLines = batchDetails.metadata?.fileAnalysis?.ratioPOs?.lines?.filter(
                                      l => l.formulaCode === first.formula_code
                                    );
                                    if (ratioLines && ratioLines.length > 0) {
                                      let wSum = 0, qSum = 0;
                                      for (const l of ratioLines) {
                                        wSum += (l.ratio ?? 0) * l.openQtyMT;
                                        qSum += l.openQtyMT;
                                      }
                                      const avgRatio = qSum > 0 ? wSum / qSum : 0;
                                      priceDisplay = <span className="text-gray-400">ratio {avgRatio.toFixed(2)}</span>;
                                    }
                                  } else if (rows.some(r => r.avg_price)) {
                                    if (!isMixed) {
                                      // Single currency — DB already computed qty-weighted avg, just use it
                                      // If multiple rows (shouldn't happen for single currency), weight by quantity
                                      let wSum = 0, qSum = 0;
                                      for (const r of rows) {
                                        if (!r.avg_price) continue;
                                        const rQty = parseFloat(r.total_remaining || '0') || parseFloat(r.total_quantity || '0');
                                        wSum += parseFloat(r.avg_price) * rQty;
                                        qSum += rQty;
                                      }
                                      const avg = qSum > 0 ? wSum / qSum : 0;
                                      const ccy = [...currencies][0] || '';
                                      priceDisplay = <>{avg.toLocaleString(undefined, { maximumFractionDigits: 2 })} {ccy}</>;
                                    } else {
                                      // Mixed — convert each sub-row to base, weighted avg by quantity
                                      let wSum = 0, qSum = 0;
                                      for (const r of rows) {
                                        if (!r.avg_price) continue;
                                        const p = parseFloat(r.avg_price);
                                        const rQty = parseFloat(r.total_remaining || '0') || parseFloat(r.total_quantity || '0');
                                        const ccy = (r.price_currency || '').toUpperCase();
                                        const { amountBase } = fx.convertToBase(p, ccy);
                                        wSum += (amountBase ?? 0) * rQty;
                                        qSum += rQty;
                                      }
                                      const avg = qSum > 0 ? wSum / qSum : 0;
                                      priceDisplay = (
                                        <span>
                                          {avg.toLocaleString(undefined, { maximumFractionDigits: 2 })} {baseCurrency}
                                          <span className="text-orange-400 ml-0.5">*</span>
                                        </span>
                                      );
                                    }
                                  }

                                  return (
                                    <tr key={key} className="text-gray-700">
                                      <td className="py-2 pr-4">
                                        <span className="inline-flex items-center gap-1.5">
                                          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getFormulaColor(first.formula_code), display: 'inline-block', flexShrink: 0 }} />
                                          <span className="font-medium" style={{ color: getFormulaColor(first.formula_code) }}>{first.formula_code}</span>
                                          <span className="text-gray-400 text-xs">({first.formula_name})</span>
                                        </span>
                                      </td>
                                      <td className="py-2 pr-4">{getSupplyTypeLabel(first.supply_type)}</td>
                                      <td className="py-2 pr-4 text-right font-mono">{totalCount}</td>
                                      <td className="py-2 pr-4 text-right font-mono">{qty.toFixed(1)}</td>
                                      <td className="py-2 text-right font-mono">{priceDisplay}</td>
                                    </tr>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                          {(() => {
                            // Check if any formula+type group had mixed currencies
                            const grouped: Record<string, Set<string>> = {};
                            for (const r of batchDetails.supplySummary) {
                              const k = `${r.formula_code}|${r.supply_type}`;
                              if (!grouped[k]) grouped[k] = new Set();
                              if (r.price_currency) grouped[k].add((r.price_currency || '').toUpperCase());
                            }
                            const hasMixed = Object.values(grouped).some(s => s.size > 1);
                            return hasMixed ? (
                              <div className="mt-1 text-[10px] text-orange-400">
                                * Mixed currencies converted to {baseCurrency} using FX rates{fx.asOf ? ` as of ${fx.asOf.substring(0, 10)}` : ''}
                              </div>
                            ) : null;
                          })()}
                          {batchDetails.metadata?.fileAnalysis?.purchaseOrders?.overdueCount ? (
                            <div className="mt-1 text-[10px] text-amber-500">
                              ⚠ {batchDetails.metadata.fileAnalysis.purchaseOrders.overdueCount} overdue PO(s) had past delivery dates and were moved to the current month
                            </div>
                          ) : null}
                        </div>
                      )}

                      {/* Unified summary for manual worker — all 3 categories in one view */}
                      {batchDetails.metadata?.workerName === 'manual' && (
                        batchDetails.supplySummary.length > 0 || batchDetails.demandSummary?.length > 0 || batchDetails.hedgeSummary?.length > 0
                      ) && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">Positions Summary</h3>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                <th className="pb-2 pr-4">Formula</th>
                                <th className="pb-2 pr-4">Category</th>
                                <th className="pb-2 pr-4">Type</th>
                                <th className="pb-2 pr-4 text-right">Count</th>
                                <th className="pb-2 text-right">Quantity (MT)</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {batchDetails.supplySummary.map((r, idx) => (
                                <tr key={`s-${idx}`} className="text-gray-700">
                                  <td className="py-1.5 pr-4">
                                    <span className="inline-flex items-center gap-1.5">
                                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: getFormulaColor(r.formula_code), display: 'inline-block', flexShrink: 0 }} />
                                      <span className="font-medium" style={{ color: getFormulaColor(r.formula_code) }}>{r.formula_code}</span>
                                    </span>
                                  </td>
                                  <td className="py-1.5 pr-4">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                      <LocalShipping sx={{ fontSize: 12 }} /> Supply
                                    </span>
                                  </td>
                                  <td className="py-1.5 pr-4 text-xs text-gray-500">{r.supply_type.replace(/_/g, ' ')}</td>
                                  <td className="py-1.5 pr-4 text-right font-mono">{r.count}</td>
                                  <td className="py-1.5 text-right font-mono">{parseFloat(r.total_remaining || r.total_quantity || '0').toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                </tr>
                              ))}
                              {batchDetails.demandSummary?.map((r, idx) => (
                                <tr key={`d-${idx}`} className="text-gray-700">
                                  <td className="py-1.5 pr-4">
                                    <span className="inline-flex items-center gap-1.5">
                                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: getFormulaColor(r.formula_code), display: 'inline-block', flexShrink: 0 }} />
                                      <span className="font-medium" style={{ color: getFormulaColor(r.formula_code) }}>{r.formula_code}</span>
                                    </span>
                                  </td>
                                  <td className="py-1.5 pr-4">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                                      <ShoppingCart sx={{ fontSize: 12 }} /> Demand
                                    </span>
                                  </td>
                                  <td className="py-1.5 pr-4 text-xs text-gray-500">{r.demand_kind.replace(/_/g, ' ')}</td>
                                  <td className="py-1.5 pr-4 text-right font-mono">{r.count}</td>
                                  <td className="py-1.5 text-right font-mono">{parseFloat(r.total_quantity || '0').toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                </tr>
                              ))}
                              {batchDetails.hedgeSummary?.map((r, idx) => (
                                <tr key={`h-${idx}`} className="text-gray-700">
                                  <td className="py-1.5 pr-4">
                                    <span className="inline-flex items-center gap-1.5">
                                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: getFormulaColor(r.formula_code), display: 'inline-block', flexShrink: 0 }} />
                                      <span className="font-medium" style={{ color: getFormulaColor(r.formula_code) }}>{r.formula_code}</span>
                                    </span>
                                  </td>
                                  <td className="py-1.5 pr-4">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                                      <TrendingUp sx={{ fontSize: 12 }} /> Hedge
                                    </span>
                                  </td>
                                  <td className="py-1.5 pr-4 text-xs text-gray-500">{r.position_side}</td>
                                  <td className="py-1.5 pr-4 text-right font-mono">{r.count}</td>
                                  <td className="py-1.5 text-right font-mono">{parseFloat(r.total_quantity || '0').toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="text-sm font-semibold text-gray-600 border-t border-gray-300">
                                <td className="pt-2" colSpan={3}>Total</td>
                                <td className="pt-2 text-right font-mono">
                                  {(batchDetails.supplySummary.reduce((s, r) => s + r.count, 0) +
                                    (batchDetails.demandSummary?.reduce((s, r) => s + r.count, 0) || 0) +
                                    (batchDetails.hedgeSummary?.reduce((s, r) => s + r.count, 0) || 0))}
                                </td>
                                <td className="pt-2 text-right font-mono">
                                  {(batchDetails.supplySummary.reduce((s, r) => s + parseFloat(r.total_remaining || r.total_quantity || '0'), 0) +
                                    (batchDetails.demandSummary?.reduce((s, r) => s + parseFloat(r.total_quantity || '0'), 0) || 0) +
                                    (batchDetails.hedgeSummary?.reduce((s, r) => s + parseFloat(r.total_quantity || '0'), 0) || 0)
                                  ).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                          {/* Manual worker file summary */}
                          {batchDetails.metadata?.fileSummary && (batchDetails.metadata.fileSummary as any).supply && (
                            <div className="mt-2 grid grid-cols-3 gap-4 text-xs text-gray-500">
                              {['supply', 'demand', 'hedges'].map(cat => {
                                const fs = (batchDetails.metadata!.fileSummary as any)[cat];
                                if (!fs) return null;
                                return (
                                  <div key={cat}>
                                    <span className="text-gray-400 capitalize">{cat}:</span> {fs.rows} rows → {fs.parsed} parsed
                                    {fs.skipped > 0 && <span className="ml-1" style={{ color: '#d97706' }}>({fs.skipped} skipped)</span>}
                                    {fs.errors > 0 && <span className="ml-1" style={{ color: '#ef4444' }}>({fs.errors} errors)</span>}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {/* Import warnings detail (collapsible) */}
                          {batchDetails.metadata?.warnings && batchDetails.metadata.warnings.length > 0 && (
                            <details className="mt-3 rounded-lg" style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
                              <summary className="flex items-center gap-1.5 p-3 cursor-pointer select-none rounded-lg transition-colors" style={{ color: '#92400e' }}>
                                <Warning fontSize="small" style={{ color: '#f59e0b' }} />
                                <span className="text-sm font-semibold">
                                  {batchDetails.metadata.warnings.length} Warning{batchDetails.metadata.warnings.length > 1 ? 's' : ''}
                                </span>
                                <span className="text-xs ml-1" style={{ color: '#9ca3af' }}>(click to expand)</span>
                              </summary>
                              <div style={{ padding: '0 12px 8px' }}>
                                {(() => {
                                  let lastCat = '';
                                  return batchDetails.metadata.warnings!.map((w, idx) => {
                                    const item = typeof w === 'string' ? null : w;
                                    const cat = item?.cat || '';
                                    const showHeader = cat && cat !== lastCat;
                                    lastCat = cat;
                                    return (
                                      <div key={idx}>
                                        {showHeader && (
                                          <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, padding: idx > 0 ? '8px 0 4px' : '2px 0 4px', borderTop: idx > 0 ? '1px solid #e5e7eb' : 'none' }}>{cat}</div>
                                        )}
                                        <div className="flex items-center" style={{ fontSize: 12, padding: '2px 0' }}>
                                          {item ? (
                                            <>
                                              <span className="shrink-0" style={{ fontFamily: 'monospace', fontSize: 10, color: '#3b82f6', background: '#eff6ff', padding: '1px 6px', borderRadius: 8, marginRight: 8 }}>#{item.row}</span>
                                              <span style={{ color: '#4b5563' }}>{item.msg}</span>
                                            </>
                                          ) : (
                                            <span style={{ color: '#4b5563' }}>{w as string}</span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            </details>
                          )}
                          {/* Import errors detail (collapsible) */}
                          {batchDetails.metadata?.errors && batchDetails.metadata.errors.length > 0 && (
                            <details className="mt-3 rounded-lg" style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
                              <summary className="flex items-center gap-1.5 p-3 cursor-pointer select-none rounded-lg transition-colors" style={{ color: '#991b1b' }}>
                                <ErrorOutline fontSize="small" style={{ color: '#ef4444' }} />
                                <span className="text-sm font-semibold">
                                  {batchDetails.metadata.errors.length} Import Error{batchDetails.metadata.errors.length > 1 ? 's' : ''}
                                </span>
                                <span className="text-xs ml-1" style={{ color: '#9ca3af' }}>(click to expand)</span>
                              </summary>
                              <div style={{ padding: '0 12px 8px' }}>
                                {(() => {
                                  let lastCat = '';
                                  return batchDetails.metadata.errors!.map((e, idx) => {
                                    const item = typeof e === 'string' ? null : e;
                                    const cat = item?.cat || '';
                                    const showHeader = cat && cat !== lastCat;
                                    lastCat = cat;
                                    return (
                                      <div key={idx}>
                                        {showHeader && (
                                          <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, padding: idx > 0 ? '8px 0 4px' : '2px 0 4px', borderTop: idx > 0 ? '1px solid #e5e7eb' : 'none' }}>{cat}</div>
                                        )}
                                        <div className="flex items-center" style={{ fontSize: 12, padding: '2px 0' }}>
                                          {item ? (
                                            <>
                                              <span className="shrink-0" style={{ fontFamily: 'monospace', fontSize: 10, color: '#3b82f6', background: '#eff6ff', padding: '1px 6px', borderRadius: 8, marginRight: 8 }}>#{item.row}</span>
                                              <span style={{ color: '#4b5563' }}>{item.msg}</span>
                                            </>
                                          ) : (
                                            <span style={{ color: '#4b5563' }}>{e as string}</span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            </details>
                          )}
                        </div>
                      )}

                      {/* Hedge positions summary (non-manual workers) */}
                      {batchDetails.metadata?.workerName !== 'manual' && (batchDetails.hedgeSummary?.length > 0 || batchDetails.metadata?.summary) && (
                        <div>
                          {/* Statement header — styled like Import Details grid */}
                          {batchDetails.metadata?.statementDate && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm mb-4">
                              <div>
                                <span className="text-gray-400 text-xs block">Statement Date</span>
                                <span className="font-medium text-gray-700">{batchDetails.metadata.statementDate}</span>
                              </div>
                              {batchDetails.metadata.brokerAccount && (
                                <div>
                                  <span className="text-gray-400 text-xs block">Broker Account</span>
                                  <span className="font-medium text-gray-700">{batchDetails.metadata.brokerAccount}</span>
                                </div>
                              )}
                              {batchDetails.metadata.commodities && (
                                <div className="col-span-2">
                                  <span className="text-gray-400 text-xs block">Commodities</span>
                                  <span className="font-medium text-gray-700">{batchDetails.metadata.commodities.join(', ')}</span>
                                </div>
                              )}
                            </div>
                          )}

                          <h3 className="text-sm font-semibold text-gray-700 mb-2">Hedge Positions</h3>

                          {/* Per-position detail table from metadata.summary */}
                          {batchDetails.metadata?.summary && batchDetails.metadata.summary.length > 0 && (
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                  <th className="pb-2 pr-4">Formula</th>
                                  <th className="pb-2 pr-4">Month</th>
                                  <th className="pb-2 pr-4">Side</th>
                                  <th className="pb-2 pr-4 text-right">Lots</th>
                                  <th className="pb-2 pr-4 text-right">MT</th>
                                  <th className="pb-2 pr-4 text-right">Avg Price</th>
                                  <th className="pb-2 text-right">Close Price</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {batchDetails.metadata.summary.map((row, idx) => {
                                  const code = row.formulaCode || row.commodity;
                                  const color = row.formulaCode ? getFormulaColor(row.formulaCode) : '#6b7280';
                                  return (
                                    <tr key={idx} className="text-gray-700">
                                      <td className="py-2 pr-4">
                                        <span className="inline-flex items-center gap-1.5">
                                          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, display: 'inline-block', flexShrink: 0 }} />
                                          <span className="font-medium" style={{ color }}>{code}</span>
                                          <span className="text-gray-400 text-xs">({row.commodity})</span>
                                          {row.isOption && (
                                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: '#EDE9FE', color: '#6D28D9' }}>
                                              {row.optionType?.toUpperCase() || 'OPT'} {row.strikePrice != null ? `@ ${row.strikePrice.toLocaleString()}` : ''}
                                            </span>
                                          )}
                                        </span>
                                      </td>
                                      <td className="py-2 pr-4">{row.month}</td>
                                      <td className="py-2 pr-4">
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${row.side === 'long' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                          {row.side}
                                        </span>
                                      </td>
                                      <td className="py-2 pr-4 text-right font-mono">{row.lots.toLocaleString()}</td>
                                      <td className="py-2 pr-4 text-right font-mono">{row.mt.toLocaleString()}</td>
                                      <td className="py-2 pr-4 text-right font-mono">{row.avgPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-gray-400">{row.currency}</span></td>
                                      <td className="py-2 text-right font-mono">{row.closePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-gray-400">{row.currency}</span></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="text-sm font-semibold text-gray-600 border-t border-gray-300">
                                  <td className="pt-2 pr-4" colSpan={3}>Total</td>
                                  <td className="pt-2 pr-4 text-right font-mono">{batchDetails.metadata.summary.reduce((s, r) => s + r.lots, 0).toLocaleString()}</td>
                                  <td className="pt-2 pr-4 text-right font-mono">{batchDetails.metadata.summary.reduce((s, r) => s + r.mt, 0).toLocaleString()}</td>
                                  <td colSpan={2}></td>
                                </tr>
                              </tfoot>
                            </table>
                          )}

                          {/* DB-level summary by formula (from hedgeSummary query) */}
                          {batchDetails.hedgeSummary?.length > 0 && (
                            <div className="mt-3">
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">By Formula</h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {batchDetails.hedgeSummary.map((h, idx) => (
                                  <div key={idx} className="flex items-start gap-2.5">
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getFormulaColor(h.formula_code), display: 'inline-block', flexShrink: 0, marginTop: 5 }} />
                                    <div>
                                      <div>
                                        <span className="text-sm font-medium" style={{ color: getFormulaColor(h.formula_code) }}>{h.formula_code}</span>
                                        <span className="text-xs text-gray-400 ml-1">({h.formula_name})</span>
                                      </div>
                                      <div className="text-xs text-gray-500 mt-0.5">
                                        {h.count} {h.position_side} · {parseFloat(h.total_quantity).toLocaleString()} MT · effect {parseFloat(h.total_hedge_effect) >= 0 ? '+' : ''}{parseFloat(h.total_hedge_effect).toLocaleString()} MT
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Warnings */}
                          {batchDetails.metadata?.warnings && batchDetails.metadata.warnings.length > 0 && (
                            <div className="mt-3">
                              {batchDetails.metadata.warnings.map((w, idx) => (
                                <div key={idx} className="text-xs text-amber-500 flex items-start gap-1.5 mt-1">
                                  <Warning sx={{ fontSize: 14 }} className="mt-0.5 shrink-0" />
                                  {typeof w === 'string' ? w : `${w.cat} #${w.row}: ${w.msg}`}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Staging summary */}
                      {batchDetails.stagingIssues.length > 0 && (
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-sm font-semibold text-gray-700">Staging Rows</h3>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'purge', batchId: batchDetails.id, batchName: batchDetails.original_filename || `#${batchDetails.id}` }); }}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                              title="Purge staging rows to free DB space"
                            >
                              <CleaningServices sx={{ fontSize: 14 }} />
                              Purge
                            </button>
                          </div>
                          <div className="flex gap-3">
                            {batchDetails.stagingIssues.map((issue, idx) => (
                              <div
                                key={idx}
                                className={`px-3 py-2 rounded-lg text-sm font-medium ${
                                  issue.parse_status === 'ok'
                                    ? 'bg-green-50 text-green-700 border border-green-200'
                                    : issue.parse_status === 'skipped'
                                    ? 'bg-gray-50 text-gray-500 border border-gray-200'
                                    : issue.parse_status === 'warning'
                                    ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                    : 'bg-red-50 text-red-700 border border-red-200'
                                }`}
                              >
                                {issue.parse_status === 'ok' ? <CheckCircle fontSize="small" className="mr-1 align-text-bottom" /> : null}
                                {issue.parse_status === 'warning' ? <Warning fontSize="small" className="mr-1 align-text-bottom" /> : null}
                                {issue.parse_status === 'error' ? <ErrorOutline fontSize="small" className="mr-1 align-text-bottom" /> : null}
                                {issue.count} {issue.parse_status}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Import job metadata */}
                      {batchDetails.metadata && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">Import Details</h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                            {batchDetails.metadata.workerName && (
                              <div>
                                <span className="text-gray-400 text-xs block">Worker</span>
                                <span className="font-medium text-gray-700">{batchDetails.metadata.workerName}</span>
                              </div>
                            )}
                            {batchDetails.metadata.durationSeconds != null && (
                              <div>
                                <span className="text-gray-400 text-xs block">Duration</span>
                                <span className="font-medium text-gray-700">{batchDetails.metadata.durationSeconds}s</span>
                              </div>
                            )}
                            {batchDetails.metadata.filesProcessed && (
                              <div className="col-span-2">
                                <span className="text-gray-400 text-xs block">Files</span>
                                <div className="flex flex-wrap gap-1.5 mt-0.5">
                                  {batchDetails.metadata.filesProcessed.map((f, i) => (
                                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-200">
                                      {f.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          {batchDetails.metadata.fileSummary && (
                            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-xs text-gray-500">
                              {batchDetails.metadata.fileSummary.contracts && (
                                <div><span className="text-gray-400">Contracts:</span> {batchDetails.metadata.fileSummary.contracts.rows} rows → {batchDetails.metadata.fileSummary.contracts.parsed} parsed</div>
                              )}
                              {batchDetails.metadata.fileSummary.purchaseOrders && (
                                <div><span className="text-gray-400">POs:</span> {batchDetails.metadata.fileSummary.purchaseOrders.rows} rows → <span className="font-medium">{batchDetails.metadata.fileSummary.purchaseOrders.open} matched</span> → {batchDetails.metadata.fileSummary.purchaseOrders.supplyPositions} positions</div>
                              )}
                              {batchDetails.metadata.fileSummary.inventory && (() => {
                                const inv = batchDetails.metadata.fileSummary!.inventory!;
                                return (
                                  <div><span className="text-gray-400">Inventory:</span> {inv.rows} rows → <span className="font-medium">{inv.matchedRows} matched</span> → {inv.materials} materials → {inv.supplyPositions} positions</div>
                                );
                              })()}
                              {batchDetails.metadata.fileSummary.gl && (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-400">GL:</span>
                                  {batchDetails.metadata.fileSummary.gl.skipped ? (
                                    <span className="text-orange-500 flex items-center gap-0.5">
                                      <Warning sx={{ fontSize: 12 }} />
                                      {batchDetails.metadata.fileSummary.gl.provided
                                        ? 'no usable entries — valuation skipped'
                                        : 'not provided — valuation skipped'}
                                    </span>
                                  ) : (
                                    <span>{batchDetails.metadata.fileSummary.gl.entries} entries</span>
                                  )}
                                </div>
                              )}
                              {batchDetails.metadata.fileSummary.mrp && (
                                <div><span className="text-gray-400">Demand:</span> {batchDetails.metadata.fileSummary.mrp.rows} rows → <span className="font-medium">{batchDetails.metadata.fileSummary.mrp.nipukRows} matched (ניפוק)</span> → {batchDetails.metadata.fileSummary.mrp.demandPositions} aggregated positions</div>
                              )}
                            </div>
                          )}
                          {batchDetails.metadata.fileAnalysis && (
                            <button
                              onClick={() => setAnalysisOpen(true)}
                              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-200 hover:bg-indigo-100 transition-colors"
                            >
                              <Analytics sx={{ fontSize: 14 }} />
                              View Detailed Analysis
                            </button>
                          )}
                          {batch.review_status === 'pending' && batch.status === 'completed' && (
                            <button
                              onClick={async () => {
                                setComparisonLoading(true);
                                setComparisonError(null);
                                try {
                                  const result = await compareBatch(batch.id);
                                  setComparison(result);
                                } catch (err: any) {
                                  setComparisonError(err.message || 'Comparison failed');
                                } finally {
                                  setComparisonLoading(false);
                                }
                              }}
                              disabled={comparisonLoading}
                              className="mt-3 ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                            >
                              <CompareArrows sx={{ fontSize: 14 }} />
                              {comparisonLoading ? 'Comparing...' : 'Compare to Current'}
                            </button>
                          )}
                          {comparisonError && (
                            <div className="mt-2 text-xs text-red-600">{comparisonError}</div>
                          )}
                          {comparison && (
                            <div className="mt-4 border border-emerald-200 rounded-lg overflow-hidden">
                              <div className="bg-emerald-50 px-4 py-2 flex items-center justify-between">
                                <h4 className="text-xs font-semibold text-emerald-800">Comparison to Active Data</h4>
                                <button onClick={() => setComparison(null)} className="text-gray-400 hover:text-gray-600 text-xs">dismiss</button>
                              </div>
                              <div className="px-4 py-3 space-y-3">
                                {/* Summary stats */}
                                <div className="grid grid-cols-4 gap-2">
                                  <div className="bg-gray-50 rounded px-3 py-2">
                                    <div className="text-[10px] text-gray-400 uppercase">Unchanged</div>
                                    <div className="text-lg font-semibold text-gray-700">{comparison.unchanged}</div>
                                  </div>
                                  <div className="bg-blue-50 rounded px-3 py-2">
                                    <div className="text-[10px] text-blue-400 uppercase">Changed</div>
                                    <div className="text-lg font-semibold text-blue-700">{comparison.changed.length}</div>
                                  </div>
                                  <div className="bg-green-50 rounded px-3 py-2">
                                    <div className="text-[10px] text-green-400 uppercase">New</div>
                                    <div className="text-lg font-semibold text-green-700">{comparison.added.length}</div>
                                  </div>
                                  <div className="bg-red-50 rounded px-3 py-2">
                                    <div className="text-[10px] text-red-400 uppercase">Removed</div>
                                    <div className="text-lg font-semibold text-red-700">{comparison.removed.length}</div>
                                  </div>
                                </div>

                                {/* Per-formula delta */}
                                {comparison.summary.byFormula.length > 0 && (
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-gray-400 border-b border-gray-100">
                                        <th className="text-left py-1 font-medium">Formula</th>
                                        <th className="text-right py-1 font-medium">Current (MT)</th>
                                        <th className="text-right py-1 font-medium">New (MT)</th>
                                        <th className="text-right py-1 font-medium">Delta</th>
                                        <th className="text-right py-1 font-medium">Positions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {comparison.summary.byFormula.map((f) => (
                                        <tr key={f.formulaCode} className="border-b border-gray-50">
                                          <td className="py-1">
                                            <span className="inline-flex items-center gap-1.5">
                                              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: getFormulaColor(f.formulaCode), display: 'inline-block', flexShrink: 0 }} />
                                              <span className="font-medium" style={{ color: getFormulaColor(f.formulaCode) }}>{f.formulaCode}</span>
                                            </span>
                                          </td>
                                          <td className="text-right py-1">{f.currentQtyMT.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                          <td className="text-right py-1">{f.newQtyMT.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                          <td className={`text-right py-1 font-medium ${f.deltaMT > 0 ? 'text-green-600' : f.deltaMT < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                            {f.deltaMT > 0 ? '+' : ''}{f.deltaMT.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                                          </td>
                                          <td className="text-right py-1 text-gray-500">{f.currentCount} → {f.newCount}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}

                                {/* Changed details */}
                                {comparison.changed.length > 0 && (
                                  <details className="text-xs">
                                    <summary className="cursor-pointer text-blue-600 font-medium">Changed positions ({comparison.changed.length})</summary>
                                    <div className="mt-1 max-h-40 overflow-y-auto">
                                      {comparison.changed.map((c, i) => (
                                        <div key={i} className="flex items-center gap-2 py-0.5 border-b border-gray-50">
                                          <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: getFormulaColor(c.formulaCode), display: 'inline-block', flexShrink: 0 }} />
                                          <span className="font-mono text-gray-600">{c.poReference}</span>
                                          <span className="font-medium" style={{ color: getFormulaColor(c.formulaCode) }}>{c.formulaCode}</span>
                                          <span className="text-gray-500">{c.changes.join(', ')}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}

                                {/* Added details */}
                                {comparison.added.length > 0 && (
                                  <details className="text-xs">
                                    <summary className="cursor-pointer text-green-600 font-medium">New positions ({comparison.added.length})</summary>
                                    <div className="mt-1 max-h-40 overflow-y-auto">
                                      {comparison.added.map((a, i) => (
                                        <div key={i} className="flex items-center gap-2 py-0.5 border-b border-gray-50">
                                          <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: getFormulaColor(a.formulaCode), display: 'inline-block', flexShrink: 0 }} />
                                          <span className="font-mono text-gray-600">{a.poReference}</span>
                                          <span className="font-medium" style={{ color: getFormulaColor(a.formulaCode) }}>{a.formulaCode}</span>
                                          <span className="text-gray-500">{a.quantity.toFixed(1)} MT</span>
                                          <span className="text-gray-400">{a.delivery}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}

                                {/* Removed details */}
                                {comparison.removed.length > 0 && (
                                  <details className="text-xs">
                                    <summary className="cursor-pointer text-red-600 font-medium">Removed positions ({comparison.removed.length})</summary>
                                    <div className="mt-1 max-h-40 overflow-y-auto">
                                      {comparison.removed.map((r, i) => (
                                        <div key={i} className="flex items-center gap-2 py-0.5 border-b border-gray-50">
                                          <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: getFormulaColor(r.formulaCode), display: 'inline-block', flexShrink: 0 }} />
                                          <span className="font-mono text-gray-600">{r.poReference}</span>
                                          <span className="font-medium" style={{ color: getFormulaColor(r.formulaCode) }}>{r.formulaCode}</span>
                                          <span className="text-gray-500">{r.quantity.toFixed(1)} MT</span>
                                          <span className="text-gray-400">{r.delivery}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}

                                {comparison.unchanged > 0 && comparison.changed.length === 0 && comparison.added.length === 0 && comparison.removed.length === 0 && (
                                  <div className="text-center text-green-600 text-xs py-2 font-medium">All positions identical to current data</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Meta info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 text-xs text-gray-500">
                        <div>
                          <span className="block text-gray-400">Created</span>
                          {formatDate(batchDetails.created_at)}
                        </div>
                        <div>
                          <span className="block text-gray-400">Completed</span>
                          {formatDate(batchDetails.completed_at)}
                        </div>
                        <div>
                          <span className="block text-gray-400">Approved</span>
                          {formatDate(batchDetails.approved_at)}
                        </div>
                        <div>
                          <span className="block text-gray-400">Type</span>
                          {batchDetails.type}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No details available</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Confirm modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              {{ approve: 'Approve Batch', reject: 'Reject Batch', delete: 'Delete Batch', purge: 'Purge Staging Rows' }[confirmAction.type]}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {confirmAction.type === 'approve' && (
                <>
                  This will approve batch <strong>{confirmAction.batchName}</strong> and make its data live.
                  {approveMode === 'replace'
                    ? ' All previously approved batches for this account will be replaced.'
                    : ' New positions will be added alongside existing data.'}
                </>
              )}
              {confirmAction.type === 'reject' && (
                <>
                  This will reject batch <strong>{confirmAction.batchName}</strong>. The imported data will not be used.
                </>
              )}
              {confirmAction.type === 'delete' && (
                <>
                  This will permanently delete batch <strong>{confirmAction.batchName}</strong> and all its data (supply positions, staging rows). This cannot be undone.
                </>
              )}
              {confirmAction.type === 'purge' && (
                <>
                  This will remove all staging rows from batch <strong>{confirmAction.batchName}</strong> to free DB space. Supply positions will be kept. This cannot be undone.
                </>
              )}
            </p>

            {confirmAction.type === 'approve' && (
              <div className="mb-4 flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-sm text-gray-600 font-medium">Mode:</span>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    onClick={() => setApproveMode('replace')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      approveMode === 'replace'
                        ? 'bg-red-50 text-red-700 border-r border-gray-300'
                        : 'bg-white text-gray-500 border-r border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Replace existing
                  </button>
                  <button
                    onClick={() => setApproveMode('add')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      approveMode === 'add'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    Add to existing
                  </button>
                </div>
              </div>
            )}

            {confirmAction.type === 'reject' && (
              <textarea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Optional: reason for rejection..."
                className="w-full border border-gray-300 rounded-lg p-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                rows={3}
              />
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setConfirmAction(null);
                  setRejectComment('');
                  setApproveMode('replace');
                }}
                disabled={processing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={
                  confirmAction.type === 'approve' ? handleApprove
                  : confirmAction.type === 'reject' ? handleReject
                  : confirmAction.type === 'delete' ? handleDelete
                  : handlePurge
                }
                disabled={processing}
                style={{
                  backgroundColor: confirmAction.type === 'approve' ? '#16a34a'
                    : confirmAction.type === 'purge' ? '#ea580c'
                    : '#dc2626',
                  color: '#fff',
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 hover:opacity-90"
              >
                {processing ? 'Processing...'
                  : confirmAction.type === 'approve' ? (approveMode === 'add' ? 'Approve & Add' : 'Approve & Replace')
                  : confirmAction.type === 'reject' ? 'Reject'
                  : confirmAction.type === 'delete' ? 'Delete Permanently'
                  : 'Purge Staging Rows'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Dialog */}
      {batchDetails?.metadata?.fileAnalysis && (
        <ImportAnalysisDialog
          open={analysisOpen}
          onClose={() => setAnalysisOpen(false)}
          analysis={batchDetails.metadata.fileAnalysis}
          batchId={batchDetails.id}
        />
      )}
    </div>
  );
};

export default ImportBatches;
